import {
  Children,
  cloneElement,
  ComponentPropsWithRef,
  ComponentType,
  CSSProperties,
  HTMLAttributes,
  isValidElement,
  memo,
  ReactNode,
  useMemo
} from 'react'
import clsx from 'clsx'
import { LinkTypeSchema } from 'cms/schemaTypes/generalSchemas/linkType'
import { PortableText, PortableTextProps } from 'next-sanity'

import { getCMSLinkParams } from 'lib/sanity/links'
import Link from 'shared/ui/Link/Link'
import { purgeWidowsAndOrphans } from 'shared/utils/common'

import styles from './CustomPortableText.module.scss'

type SerializableComponent = ComponentType<HTMLAttributes<HTMLElement>>

const serialize =
  (Component: SerializableComponent) =>
  (props: HTMLAttributes<HTMLElement>) => {
    const isEmptyString = (child: ReactNode) =>
      typeof child === 'string' && child.trim() === ''

    if (
      Array.isArray(props.children) &&
      props.children.length === 1 &&
      isEmptyString(props.children?.[0] as ReactNode)
    ) {
      return <br />
    }

    return <Component {...props} />
  }

const formatText = (children: ReactNode, preventWidow: boolean): ReactNode => {
  return Children.map(children, (child: ReactNode) => {
    if (typeof child === 'string') {
      // Format direct text
      return purgeWidowsAndOrphans(child, preventWidow)
    }

    // Format text in child components (one level deep)
    if (isValidElement(child)) {
      const props = child.props as { children: ReactNode[] }
      const subChildren = props.children

      const formattedSubChildren = Children.map(subChildren, subChild => {
        if (typeof subChild === 'string') {
          return purgeWidowsAndOrphans(subChild, preventWidow)
        }

        return subChild
      })

      return cloneElement(child, undefined, formattedSubChildren)
    }

    return child
  })
}

const formatAndSerialize = (
  Component: SerializableComponent,
  preventWidow: boolean
) => {
  return ({ children }: { children: ReactNode }) => {
    const formatted = formatText(children, preventWidow)

    return serialize(Component)({ children: formatted })
  }
}

interface CustomPortableTextProps extends PortableTextProps {
  // Disable joining the paragraph's last two words with a non-breaking space.
  // Useful for narrow containers (e.g. step captions) where forcing the last
  // two words onto one unbreakable line would overflow instead of wrapping.
  preventWidow?: boolean
}

const CustomPortableText: React.FC<CustomPortableTextProps> = memo(
  function CustomPortableTextInner({
    components: _,
    preventWidow = true,
    ...props
  }: CustomPortableTextProps) {
    const blockParsers = useMemo(
      () => ({
        h1: formatAndSerialize(H1Sanity, preventWidow),
        h2: formatAndSerialize(H2Sanity, preventWidow),
        h3: formatAndSerialize(H3Sanity, preventWidow),
        h4: formatAndSerialize(H4Sanity, preventWidow),
        h5: formatAndSerialize(H5Sanity, preventWidow),
        h6: formatAndSerialize(H6Sanity, preventWidow),
        tiny: formatAndSerialize(PTinySanity, preventWidow),
        small: formatAndSerialize(PSmallSanity, preventWidow),
        normal: formatAndSerialize(PSanity, preventWidow),
        medium: formatAndSerialize(PMediumSanity, preventWidow),
        large: formatAndSerialize(PLargeSanity, preventWidow),
        extraLarge: formatAndSerialize(PExtraLargeSanity, preventWidow),
        sectionHeader: formatAndSerialize(H2SectionHeader, preventWidow)
      }),
      [preventWidow]
    )

    return (
      <PortableText
        components={{
          marks: {
            right: ({ children }) => RightDecorator({ children }),
            center: ({ children }) => CenterDecorator({ children }),
            color: ({ children, value }) =>
              ColorDecorator({
                children,
                value: value as { color?: { hex: string } }
              }),
            link: ({ children, value }) =>
              LinkDecorator({
                children,
                value: value as { link?: LinkTypeSchema }
              }),
            strong: ({ children }) => <StyledStrong>{children}</StyledStrong>
          },

          block: {
            h1: ({ children }) => blockParsers.h1({ children }),
            h2: ({ children }) => blockParsers.h2({ children }),
            h3: ({ children }) => blockParsers.h3({ children }),
            h4: ({ children }) => blockParsers.h4({ children }),
            h5: ({ children }) => blockParsers.h5({ children }),
            h6: ({ children }) => blockParsers.h6({ children }),
            tiny: ({ children }) => blockParsers.tiny({ children }),
            small: ({ children }) => blockParsers.small({ children }),
            normal: ({ children }) => blockParsers.normal({ children }),
            medium: ({ children }) => blockParsers.medium({ children }),
            large: ({ children }) => blockParsers.large({ children }),
            extraLarge: ({ children }) => blockParsers.extraLarge({ children }),
            sectionHeader: ({ children }) =>
              blockParsers.sectionHeader({ children }),
            a: ({ children, value }) =>
              LinkDecorator({
                children,
                value: value as { link?: LinkTypeSchema }
              })
          },

          list: {
            bullet: ({ children }) => (
              <StyledUlList>{formatText(children, preventWidow)}</StyledUlList>
            ),
            number: ({ children }) => (
              <StyledOlList>{formatText(children, preventWidow)}</StyledOlList>
            )
          }
        }}
        {...props}
      />
    )
  }
)

export const CenterDecorator = (props: { children: ReactNode }) => {
  return <span className={styles.center}>{props.children}</span>
}

export const RightDecorator = (props: { children: ReactNode }) => {
  return <span className={styles.right}>{props.children}</span>
}

export const ColorDecorator = (props: {
  children: ReactNode
  value: { color?: { hex: string } } | string
}) => {
  const color =
    typeof props.value === 'string'
      ? props.value
      : props?.value?.color?.hex || 'inherit'

  return (
    <span
      className={styles.colorDecorator}
      style={{ '--cpt-color': color } as CSSProperties}
    >
      {props.children}
    </span>
  )
}

const StyledStrong = ({
  className,
  ...props
}: ComponentPropsWithRef<'strong'>) => (
  <strong className={clsx(styles.strong, className)} {...props} />
)

export const H1Sanity = ({ children }: HTMLAttributes<HTMLHeadingElement>) => {
  if (
    Array.isArray(children) &&
    children?.length === 1 &&
    children?.[0] === ''
  ) {
    return <br />
  }

  return <h1>{children}</h1>
}

export const H2Sanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'h2'>) => (
  <h2 className={clsx(styles.h2, className)} {...props} />
)

export const H3Sanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'h3'>) => (
  <h3 className={clsx(styles.h3, className)} {...props} />
)

export const H4Sanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'h4'>) => (
  <h4 className={clsx(styles.h4, className)} {...props} />
)

export const H5Sanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'h5'>) => (
  <h5 className={clsx(styles.h5, className)} {...props} />
)

export const H6Sanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'h6'>) => (
  <h6 className={clsx(styles.h6, className)} {...props} />
)

export const PTinySanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'p'>) => (
  <p className={clsx(styles.pTiny, className)} {...props} />
)

export const PSmallSanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'p'>) => (
  <p className={clsx(styles.pSmall, className)} {...props} />
)

export const PSanity = ({ className, ...props }: ComponentPropsWithRef<'p'>) => (
  <p className={clsx(styles.p, className)} {...props} />
)

export const PMediumSanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'p'>) => (
  <p className={clsx(styles.pMedium, className)} {...props} />
)

export const PLargeSanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'p'>) => (
  <p className={clsx(styles.pLarge, className)} {...props} />
)

export const PExtraLargeSanity = ({
  className,
  ...props
}: ComponentPropsWithRef<'p'>) => (
  <p className={clsx(styles.pExtraLarge, className)} {...props} />
)

export const H2SectionHeader = ({
  className,
  ...props
}: ComponentPropsWithRef<'h2'>) => (
  <h2 className={clsx(styles.sectionHeader, className)} {...props} />
)

export const StyledUlList = ({
  className,
  ...props
}: ComponentPropsWithRef<'ul'>) => (
  <ul className={clsx(styles.ulList, className)} {...props} />
)

export const StyledOlList = ({
  className,
  ...props
}: ComponentPropsWithRef<'ol'>) => (
  <ol className={clsx(styles.olList, className)} {...props} />
)

export const LinkDecorator = (props: {
  children: ReactNode
  value: { link?: LinkTypeSchema }
}) => {
  const link =
    'value' in props && 'link' in props.value ? props.value?.link : null

  if (!link) return null

  const getSanityLinkParams = getCMSLinkParams(link)

  return (
    <Link
      href={getSanityLinkParams?.href ?? ''}
      target='_blank'
      rel='noopener noreferrer'
    >
      {props.children}
    </Link>
  )
}

export default CustomPortableText
